import { useState } from 'react';
import { ImageOff, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveMediaKind } from '../lib/attachments';
import { MediaViewerDialog, type MediaViewerState } from './MediaViewerDialog';

type LabMediaThumbProps = {
  previewUrl?: string | null;
  mediaFileType?: string | null;
  mediaContentType?: string | null;
  className?: string;
  /** When false, thumb is display-only (no lightbox). Default true when URL present. */
  openLightbox?: boolean;
};

export function isLabVideoFixture(
  mediaFileType: string | null | undefined,
  mediaContentType: string | null | undefined,
  previewUrl: string | null | undefined,
): boolean {
  if ((mediaContentType ?? '').toLowerCase().startsWith('video/')) return true;
  return resolveMediaKind(mediaFileType, previewUrl) === 'VID';
}

/** Compact image/video thumb for Pipeline Lab; click opens the shared media lightbox. */
export function LabMediaThumb({
  previewUrl,
  mediaFileType,
  mediaContentType,
  className,
  openLightbox = true,
}: LabMediaThumbProps) {
  const [viewer, setViewer] = useState<MediaViewerState | null>(null);
  const box = cn('relative size-16 shrink-0 overflow-hidden rounded bg-muted', className);
  const video = isLabVideoFixture(mediaFileType, mediaContentType, previewUrl);

  if (!previewUrl) {
    return (
      <div
        className={cn('flex items-center justify-center text-muted-foreground', box)}
        title="No media"
        aria-label="No media"
      >
        <ImageOff className="size-5" />
      </div>
    );
  }

  const media = video ? (
    <>
      <video
        src={previewUrl}
        className="size-full object-cover"
        muted
        playsInline
        preload="metadata"
        aria-hidden
      />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30 text-white">
        <Play className="size-5 fill-current" />
      </span>
    </>
  ) : (
    <img src={previewUrl} alt="" className="size-full object-cover" />
  );

  return (
    <>
      {openLightbox ? (
        <button
          type="button"
          onClick={() =>
            setViewer({ url: previewUrl, isVideo: video, playbackId: null })
          }
          className={cn(
            box,
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          aria-label={video ? 'Open video preview' : 'Open image preview'}
        >
          {media}
        </button>
      ) : (
        <div className={box}>{media}</div>
      )}
      <MediaViewerDialog media={viewer} onClose={() => setViewer(null)} />
    </>
  );
}
